<template>
  <ContentContainer :loadingStatus="isLoading">
    <div class="terms-of-payment-list content-wrapper">
      <div class="page-header">{{ $t('termsOfPayment') }}</div>
      <div>
        <el-row>
          <el-col :span="4">
            <el-input :placeholder="$t('keyword')"
                      @input="isSearching = true"
                      v-model="keyword"></el-input>
          </el-col>
          <el-col :span="3" style="padding: 0 5px;">
            <el-select v-model="status"
                       @change="isSearching = true"
                       :placeholder="$t('all')">
              <el-option :label="$t('all')" value=""></el-option>
              <el-option
                :label="$t('STATUS.ENABLE')"
                :value="STATUS.ENABLE">
              </el-option>
              <el-option
                :label="$t('STATUS.DISABLE')"
                :value="STATUS.DISABLE">
              </el-option>
            </el-select>
          </el-col>
          <el-col :span="17" style="text-align: right;">
            <router-link to="terms-of-payment/add" style="float: right;">
              <el-button type="primary" icon="el-icon-plus"> {{ $t('newTermsOfPayment') }}</el-button>
            </router-link>
          </el-col>
        </el-row>
        <div style="margin-top: 25px">
          <el-table
            id="terms-of-payment-list-table"
            :data="termsOfPaymentListDataSource"
            v-loading="isSearching"
            header-row-class-name="table-header-color"
            :default-sort = "{prop: sortKey, order: sortBy}"
            style="width: 100%">
            <el-table-column
              prop="name"
              :label="$t('title')">
            </el-table-column>
            <el-table-column
              :label="$t('detail')">
              <template slot-scope="scope">
                <div class="detail">{{ scope.row.detail }}</div>
              </template>
            </el-table-column>
            <el-table-column
              :label="$t('status')"
              width="80px">
              <template slot-scope="scope">
                <el-tag :type="scope.row.isEnable === 1 ? 'success' : 'danger'" size="mini" disable-transitions>
                  {{ $t(getStatusName(scope.row.isEnable)) }}
                </el-tag>
              </template>
            </el-table-column>
            <el-table-column align="center">
              <template slot-scope="scope">
                <el-button type="primary" size="small" icon="el-icon-edit" plain circle @click="onEditButtonClick(scope.row)"></el-button>
                <el-button type="danger" size="small" icon="el-icon-delete" plain circle @click="onRemoveButtonClick(scope.row.id, scope.row.name)"></el-button>
              </template>
            </el-table-column>
          </el-table>
          <div style="margin-top: 10px">
            <el-pagination
              background
              layout="prev, pager, next"
              :total="filterTermsOfPayment().length"
              :page-size="pageSize"
              :pager-count="11"
              :current-page="currentPage"
              @current-change="changePage">
            </el-pagination>
          </div>
        </div>
      </div>
    </div>
  </ContentContainer>
</template>

<script>
import i18nMessages from '@/translations/Setting/ManageSetting/TermsOfPayment/List.i18n'
import AlertViewMixin from '@/mixins/alert-view-mixin'
import LoadingViewMixin from '@/mixins/loading-view-mixin'
import NotificationMixin from '@/mixins/notification-mixin'
import SettingTermsOfPaymentApi from '@/api/setting/terms-of-payment-api'
import ContentContainer from '@/components/ContentContainerComponent.vue'
import { filterDataByKeyword, adjustCurrentPage, getDataOnPaging } from '@/services/general-service'
import { getStatusName } from '@/helpers'
import { STATUS } from '@/configs/constant'
import { debounce } from 'lodash'

export default {
  name: 'SettingTermsOfPaymentList',
  mixins: [AlertViewMixin, LoadingViewMixin, NotificationMixin],
  components: { ContentContainer },
  i18n: { messages: i18nMessages },
  watch: {
    keyword: debounce(function () {
      this.isSearching = false
    }, 300),
    status: debounce(function () {
      this.isSearching = false
    }, 300),
    isSearching: function (value) {
      if (!value) {
        this.getTermsOfPaymentListDataSource()
      }
    }
  },
  data () {
    return {
      STATUS,
      getStatusName,
      isLoading: true,
      keyword: '',
      isSearching: false,
      status: '',
      sortKey: 'name',
      sortBy: 'ascending',
      currentPage: 1,
      pageSize: 10,
      termsOfPaymentList: [],
      termsOfPaymentListDataSource: []
    }
  },
  created: async function () {
    try {
      this.isLoading = true
      this.termsOfPaymentList = await SettingTermsOfPaymentApi.list()

      this.termsOfPaymentList.sort((prev, next) => {
        return (prev.name < next.name) ? -1 : 1
      })
      this.getTermsOfPaymentListDataSource()
    } catch (err) {
      this.alertError(err.message)
    }

    this.isLoading = false
  },
  methods: {
    getTermsOfPaymentListDataSource: function () {
      const filteredTermsOfPaymentList = this.filterTermsOfPayment()
      filteredTermsOfPaymentList.sort((a, b) => {
        return (a.name < b.name) ? -1 : 1
      })
      this.currentPage = adjustCurrentPage(filteredTermsOfPaymentList.length, this.currentPage, this.pageSize)
      const termsOfPaymentList = getDataOnPaging(filteredTermsOfPaymentList, this.currentPage, this.pageSize)
      this.termsOfPaymentListDataSource = [...termsOfPaymentList]
    },
    filterTermsOfPayment: function () {
      const searchKeys = ['name', 'detail']
      let filteredTermsOfPaymentList = filterDataByKeyword(this.termsOfPaymentList, this.keyword, searchKeys)

      if (this.status !== '') {
        filteredTermsOfPaymentList = filteredTermsOfPaymentList.filter(item => item.isEnable === this.status)
      }

      return filteredTermsOfPaymentList
    },
    changePage: function (page) {
      this.currentPage = page
      this.getTermsOfPaymentListDataSource()
    },
    onEditButtonClick: function (termsOfPayment) {
      this.$router.push({
        name: 'SettingTermsOfPaymentEdit',
        params: {
          termsOfPayment: termsOfPayment
        }
      })
    },
    onRemoveButtonClick: async function (termsOfPaymentId, name) {
      const confirmMessage = this.$t('confirmRemoveTermsOfPaymentMessage', {
        name: `<strong>${name}</strong>`
      })
      const confirmButtonText = this.$t('confirmButtonText')
      const cancelButtonText = this.$t('cancelButtonText')
      const isConfirmRemove = await this.alertConfirmDelete(confirmMessage, confirmButtonText, cancelButtonText)

      if (!isConfirmRemove) {
        return false
      }

      this.removeTermsOfPayment(termsOfPaymentId, name)
    },
    removeTermsOfPayment: async function (termsOfPaymentId, name) {
      try {
        this.startLoading()

        await SettingTermsOfPaymentApi.remove(termsOfPaymentId)
        this.termsOfPaymentList = this.termsOfPaymentList.filter(item => item.id !== termsOfPaymentId)
        this.getTermsOfPaymentListDataSource()

        const notificationTitle = this.$t('removeTermsOfPaymentSuccess', {
          name: name
        })
        this.showNotificationSuccess(notificationTitle)
      } catch (err) {
        this.alertError(err.message)
      }

      this.stopLoading()
    }
  }
}
</script>

<style scoped>
  #terms-of-payment-list-table {
    width: 100%;
    border: 1px solid #dddddd;
    border-radius: 5px;
    box-shadow: 0px 3px 5px 1px #eeeeee;
  }
  .el-table th {
    background-color: #eeeeee;
  }
  .el-form > div {
    text-align: left;
  }
  .detail {
    white-space: pre-line;
  }
</style>
